/** Lee y redimensiona un logo de colegio a data URL (apto para localStorage). */

const MAX_EDGE_PX = 256;
const MAX_INPUT_BYTES = 2.5 * 1024 * 1024;
const JPEG_QUALITY = 0.85;

const ALLOWED_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
]);

export function isSchoolLogoFile(file: File): boolean {
  const type = String(file.type || '').toLowerCase();
  if (ALLOWED_TYPES.has(type)) return true;
  // Algunos SO no informan MIME; aceptar por extensión.
  return /\.(png|jpe?g|webp|gif)$/i.test(file.name);
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('school_logo_invalid'));
    };
    img.src = url;
  });
}

/**
 * Convierte un archivo de imagen en data URL JPEG/PNG redimensionado.
 * PNG se conserva si tiene transparencia aproximada (alpha); si no, JPEG.
 */
export async function fileToSchoolLogoDataUrl(file: File): Promise<string> {
  if (!isSchoolLogoFile(file)) {
    throw new Error('school_logo_type');
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('school_logo_too_large');
  }

  const img = await loadImageFromFile(file);
  const srcW = Math.max(1, img.naturalWidth || img.width);
  const srcH = Math.max(1, img.naturalHeight || img.height);
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('school_logo_canvas');

  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const preferPng =
    file.type === 'image/png' ||
    file.type === 'image/webp' ||
    /\.(png|webp)$/i.test(file.name);

  if (preferPng) {
    return canvas.toDataURL('image/png');
  }
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

export function mapSchoolLogoError(code: string): string {
  const map: Record<string, string> = {
    school_logo_type: 'Usa una imagen PNG, JPG o WebP.',
    school_logo_too_large: 'La imagen es demasiado grande (máx. 2,5 MB).',
    school_logo_invalid: 'No se pudo leer la imagen. Prueba con otro archivo.',
    school_logo_canvas: 'No se pudo procesar la imagen en este navegador.',
  };
  return map[code] ?? 'No se pudo subir el logo.';
}
